import { Component, Host, Prop, h, Element } from '@stencil/core';
import '@material/web/iconbutton/filled-icon-button';
import '@material/web/iconbutton/icon-button';
import '@material/web/iconbutton/outlined-icon-button';
import '@material/web/icon/icon';

@Component({
  tag: 'omk-icon-button',
  styleUrl: 'omk-icon-button.scss',
  shadow: { delegatesFocus: true },
})
export class OmkIconButton {
  @Prop({ reflect: true }) buttonType: 'filled-icon-button' | 'outlined-icon-button' | 'icon-button' | 'standard-icon-button' =
    'standard-icon-button';
  @Prop() buttonSize: string = 'regular';
  @Prop({ reflect: true }) ghostMode: boolean = false;
  @Prop() disabled: boolean = false;
  @Prop() ariaLabel: string | null;

  @Element() el: HTMLElement;

  private verifiedButtonType = '';
  private validButtonTypes = ['filled-icon-button', 'outlined-icon-button', 'icon-button'];

  componentWillLoad(): void {
    this.verifiedButtonType = this.convertToMaterialButtonType(this.buttonType);
  }

  componentDidRender(): void {
    const outlinedButtonElementName = 'md-outlined-icon-button';
    const filledButtonElementName = 'md-filled-icon-button';

    const icon = this.el.querySelector('omk-icon');
    if (icon) {
      const mdIcon = icon.shadowRoot.querySelector('md-icon');
      mdIcon.style.fontSize = 'inherit';
    }

    const mdButton = this.el.shadowRoot.querySelector(this.verifiedButtonType);
    const mdShadow = mdButton.shadowRoot;
    const elemStyleSheets = mdShadow.adoptedStyleSheets;
    if (!elemStyleSheets) return;
    const sheet = new CSSStyleSheet();
    if (this.verifiedButtonType === filledButtonElementName) {
      sheet.replaceSync(`:host button:not([disabled])
      {
        background: var(--_omk-filled-icon-button-container-color);

        &:hover {
          background: var(--_omk-filled-icon-button-hover-state-layer-color);
        }
      }`);

      mdShadow.adoptedStyleSheets = [...elemStyleSheets, sheet];
    }

    if (this.verifiedButtonType === outlinedButtonElementName && !this.ghostMode) {
      sheet.replaceSync(`:host button:not([disabled])
      {
        background-color: var(--_omk-outlined-icon-button-container-color);

        &:hover {
          background-color: var(--_omk-outlined-icon-button-hover-state-layer-color);
        }

        md-ripple {
          z-index: -2;
        }
      }

      :host([disabled])
      {
        background-color: var(--_omk-outlined-icon-button-disabled-container-color);
        border-radius: var(--_omk-outlined-icon-button-container-shape);
      }`);

      mdShadow.adoptedStyleSheets = [...elemStyleSheets, sheet];
    }

    if (this.verifiedButtonType === outlinedButtonElementName && this.ghostMode) {
      sheet.replaceSync(`:host button:not([disabled])
      {
        &:hover {
          background-color: var(--_omk-outlined-icon-button-hover-state-layer-color);
        }

        md-ripple {
          z-index: -2;
        }
      }

      :host([disabled])
      {
        background-color: var(--_omk-outlined-icon-button-disabled-container-color);
        border-radius: var(--_omk-outlined-icon-button-container-shape);
      }`);

      mdShadow.adoptedStyleSheets = [...elemStyleSheets, sheet];
    }
  }

  convertToMaterialButtonType(input: string): string {
    let type = 'md-icon-button';
    const lowerCaseInput = input.toLowerCase();
    if (lowerCaseInput === 'standard-icon-button') {
      type = 'md-icon-button';
    } else if (this.validButtonTypes.includes(lowerCaseInput)) {
      type = `md-${lowerCaseInput}`;
    }

    return type;
  }

  render() {
    const isStandardBtn = this.buttonType.toLowerCase() === 'standard-icon-button';
    return (
      <Host disabled={this.disabled}>
        <this.verifiedButtonType
          part="button"
          type="button"
          class={{
            'btn': true,
            'ghost-mode': this.ghostMode,
            'btn-large': this.buttonSize.toLowerCase() === 'large',
            'btn-regular': !(this.buttonSize.toLowerCase() === 'large'),
            'standard-btn': isStandardBtn,
          }}
          disabled={this.disabled}
          aria-label={this.ariaLabel}
        >
          <div>
            <slot></slot>
          </div>
        </this.verifiedButtonType>
      </Host>
    );
  }
}
